;!function(){try{var e="undefined"!=typeof window?window:"undefined"!=typeof global?global:"undefined"!=typeof globalThis?globalThis:"undefined"!=typeof self?self:{};e._sentryModuleMetadata=e._sentryModuleMetadata||{},e._sentryModuleMetadata[(new e.Error).stack]=Object.assign({},e._sentryModuleMetadata[(new e.Error).stack],{"_sentryBundlerPluginAppKey:native-teams-frontend":true});}catch(e){}}();import { Fragment } from 'react';
import { Label } from '@workspace/ui/components/label';
import {
  Toggle as TogglePrimitive,
  ToggleTabsList as ToggleTabsListPrimitive,
  ToggleTabs as ToggleTabsPrimitive,
  ToggleTabsTrigger as ToggleTabsTriggerPrimitive,
} from '@workspace/ui/components/toggle';
import { Tooltip, TooltipContent, TooltipTrigger } from '@workspace/ui/components/tooltip';
import { cn } from '@workspace/ui/lib';

interface ToggleProps {
  label?: string;
  description?: string;
  disabled?: boolean;
  value?: boolean;
  defaultValue?: boolean;
  onChange?: (value: boolean) => void;
  className?: string;
  id?: string;
  Icon?: React.ComponentType<{ className?: string }>;
  iconClassName?: string;
  box?: boolean;
  labelClassName?: string;
}
export const Toggle = ({
  label,
  description,
  disabled,
  value,
  defaultValue,
  onChange,
  className,
  id,
  Icon,
  iconClassName,
  box,
  labelClassName,
}: ToggleProps) => {
  return (
    <div
      className={cn(
        'flex justify-between gap-3',
        box
          ? 'border-grey-400 dark:bg-grey-1000 rounded-2xl border p-6 dark:border-transparent'
          : '',
        description ? 'items-start' : 'items-center',
        className
      )}
    >
      <div className={description ? 'flex flex-col gap-2' : ''}>
        <div className="flex items-center gap-2">
          {Icon && <Icon className={iconClassName || 'h-5 w-5 text-pink-400 dark:text-pink-400'} />}
          <Label
            className={cn(
              'text-sm font-medium text-neutral-200 dark:text-neutral-100',
              labelClassName
            )}
            htmlFor={id}
          >
            {label}
          </Label>
        </div>
        {description && <p className="text-grey-600 dark:text-grey-500 text-sm">{description}</p>}
      </div>
      <TogglePrimitive
        onCheckedChange={onChange}
        checked={value}
        defaultChecked={defaultValue}
        disabled={disabled}
        className={className}
        id={id}
      />
    </div>
  );
};

export interface ToggleTabsSettings {
  label?: string;
  disabled?: boolean;
  /** Shown on hover when the tab is disabled (wrapper receives pointer events). */
  disabledTooltip?: string;
  value: string;
  onValueChange?: (value: string) => void;
  icon?: React.ComponentType<{ className?: string }>;
}
interface ToggleTabsProps {
  value: string;
  className?: string;
  listClassName?: string;
  triggerClassName?: string;
  settings: ToggleTabsSettings[];
  onValueChange?: (value: string) => void;
  hideInactiveLabels?: boolean;
  /** When false the list is rendered without the fixed-height scroll wrapper. */
  scrollable?: boolean;
}

export const ToggleTabs = ({
  value,
  settings,
  className,
  listClassName,
  triggerClassName,
  onValueChange,
  hideInactiveLabels = false,
  scrollable = true,
}: ToggleTabsProps) => {
  const equalWidthTabSlotClassName =
    'flex h-[calc(100%-1px)] min-w-0 flex-1 basis-0 [&_[data-slot=tabs-trigger]]:w-full';

  return (
    <ToggleTabsPrimitive value={value} className={className} onValueChange={onValueChange}>
      <ToggleTabsListPrimitive
        hideInactiveLabels={hideInactiveLabels}
        className={listClassName}
        scrollable={scrollable}
      >
        {settings.map((setting) => {
          const Icon = setting.icon;
          const isActive = value === setting.value;

          const trigger = (
            <ToggleTabsTriggerPrimitive
              value={setting.value}
              disabled={setting.disabled}
              className={cn(
                'flex items-center gap-2',
                scrollable && setting.disabled && setting.disabledTooltip && 'w-full',
                hideInactiveLabels && !isActive && 'justify-center',
                triggerClassName
              )}
            >
              {Icon && <Icon className="h-4 w-4" />}
              {(!hideInactiveLabels || isActive) && setting.label}
            </ToggleTabsTriggerPrimitive>
          );

          if (setting.disabled && setting.disabledTooltip) {
            const disabledTabWithTooltip = (
              <Tooltip>
                <TooltipTrigger asChild>
                  <span
                    tabIndex={0}
                    aria-label={setting.disabledTooltip}
                    className={cn(
                      'focus-visible:ring-ring flex cursor-not-allowed rounded-sm outline-none focus-visible:ring-2 focus-visible:ring-offset-2',
                      scrollable ? 'h-[calc(100%-1px)] min-w-0 flex-1' : 'h-full w-full'
                    )}
                  >
                    {trigger}
                  </span>
                </TooltipTrigger>
                <TooltipContent>
                  <p>{setting.disabledTooltip}</p>
                </TooltipContent>
              </Tooltip>
            );

            if (scrollable) {
              return <Fragment key={setting.value}>{disabledTabWithTooltip}</Fragment>;
            }

            return (
              <span key={setting.value} className={equalWidthTabSlotClassName}>
                {disabledTabWithTooltip}
              </span>
            );
          }

          if (scrollable) {
            return <Fragment key={setting.value}>{trigger}</Fragment>;
          }

          return (
            <span key={setting.value} className={equalWidthTabSlotClassName}>
              {trigger}
            </span>
          );
        })}
      </ToggleTabsListPrimitive>
    </ToggleTabsPrimitive>
  );
};
